import React from 'react';
import { PERMISSION_TYPE } from '../../../../constants/featurePermissionsConstant';
import { Checkbox, Divider, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import './UserPermissionCheckBoxes.scss';
import { LocalStorageService } from '../../../../services/localStorageService';

type UserPermissionCheckBoxesProps = {
	isEditUser?: boolean;
	user: any;
	featurePermission: any[];
	handleFeaturesChange: (_e: any, _featurePermission: any) => void;
	handlePermissionCheckBoxForCreateNewAccount: (_featureId: string) => boolean;
	handleFeaturesChangeInEdit: (_e: any, _featurePermission: any) => void;
	handleFeatureExist: () => boolean;
};

const UserPermissionCheckBoxes = ({
	isEditUser = false,
	user,
	featurePermission,
	handleFeaturesChange,
	handlePermissionCheckBoxForCreateNewAccount,
	handleFeaturesChangeInEdit,
	handleFeatureExist,
}: UserPermissionCheckBoxesProps) => {
	const { t } = useTranslation();
	const administratorPermissions: any[] = [];
	const userPermissions: any[] = [];
	const products: any[] = [];

	featurePermission.forEach((permission) => {
		if (permission.type === PERMISSION_TYPE.administratorPermissions) {
			administratorPermissions.push(permission);
		}

		if (permission.type === PERMISSION_TYPE.userPermissions) {
			userPermissions.push(permission);
		}

		if (permission.type === PERMISSION_TYPE.products) {
			products.push(permission);
		}
	});

	const isDisabled = user?.userId === LocalStorageService.getUserId();
	const onCheckBoxChange = (e: any) => {
		isEditUser ? handleFeaturesChangeInEdit(e, featurePermission) : handleFeaturesChange(e, featurePermission);
	};
	const getCheckedValue: (_id: string, _featurePermission: any[]) => boolean = isEditUser
		? handleFeatureExist
		: handlePermissionCheckBoxForCreateNewAccount;

	return (
		<div className="permissionCheckBoxesWrapper">
			{administratorPermissions.length > 0 && (
				<div className="permissionGroup">
					<label
						style={{ fontWeight: 'bold' }}
						className="permissionLabel"
						htmlFor={'allAdministratorPermissions'}
					>
						<span>{t('SETTING_USER_PERMISSIONS_ADMINISTRATOR_NAME')}</span>
					</label>

					<div style={{ marginTop: 5 }} key={'allAdministratorPermissions'} className="userAppPermission">
						<Checkbox
							id={'allAdministratorPermissions'}
							onChange={onCheckBoxChange}
							value={'administratorPermissions'}
							checked={getCheckedValue('administratorPermissions', featurePermission)}
							disabled={isDisabled}
							style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
						/>
						<label className="permissionLabel" htmlFor={'allAdministratorPermissions'}>
							<span>{t('SETTING_USER_PERMISSIONS_PERMISSIONS_ALL_OPTIONS')}</span>
						</label>
					</div>
					<div style={{ marginTop: 5, marginBottom: 5 }}>
						<Divider />
					</div>

					{administratorPermissions.map((permission) => {
						return (
							<div key={permission.featureId} className="userAppPermission">
								<Checkbox
									id={permission.featureId}
									onChange={onCheckBoxChange}
									value={permission.featureId}
									checked={getCheckedValue(permission.featureId, featurePermission)}
									disabled={isDisabled}
									style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
								/>
								<Tooltip
									title={
										<Typography style={{ color: '#ffffff' }}>{permission.description}</Typography>
									}
									placement="bottom"
								>
									<label className="permissionLabel" htmlFor={permission.featureId}>
										{permission.name}
									</label>
								</Tooltip>
							</div>
						);
					})}
				</div>
			)}
			{userPermissions.length > 0 && (
				<div className="permissionGroup">
					<label
						style={{ fontWeight: 'bold' }}
						className="permissionLabel"
						htmlFor={'allAdministratorPermissions'}
					>
						<span>{t('SETTING_USER_PERMISSIONS_USER_NAME')}</span>
					</label>
					<div style={{ marginTop: 5 }} key={'allUserPermissions'} className="userAppPermission">
						<Checkbox
							id={'allUserPermissions'}
							disabled={isDisabled}
							style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
							onChange={onCheckBoxChange}
							value={'userPermissions'}
							checked={getCheckedValue('userPermissions', featurePermission)}
						/>
						<label className="permissionLabel" htmlFor={'allUserPermissions'}>
							<span>{t('SETTING_USER_PERMISSIONS_PERMISSIONS_ALL_OPTIONS')}</span>
						</label>
					</div>
					<div style={{ marginTop: 5, marginBottom: 5 }}>
						<Divider />
					</div>
					{userPermissions.map((permission) => {
						return (
							<div key={permission.featureId} className="userAppPermission">
								<Checkbox
									id={permission.featureId}
									disabled={isDisabled}
									style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
									onChange={onCheckBoxChange}
									value={permission.featureId}
									checked={getCheckedValue(permission.featureId, featurePermission)}
								/>

								<Tooltip
									title={
										<Typography style={{ color: '#ffffff' }}>{permission.description}</Typography>
									}
									placement="bottom"
								>
									<label className="permissionLabel" htmlFor={permission.featureId}>
										{permission.name}
									</label>
								</Tooltip>
							</div>
						);
					})}
				</div>
			)}
			{products.length > 0 && (
				<div className="permissionGroup">
					<label
						style={{ fontWeight: 'bold' }}
						className="permissionLabel"
						htmlFor={'allAdministratorPermissions'}
					>
						<span>{t('SETTING_USER_PERMISSIONS_PRODUCTS_NAME')}</span>
					</label>
					<div style={{ marginTop: 5 }} key={'allProducts'} className="userAppPermission">
						<Checkbox
							id={'allProducts'}
							disabled={isDisabled}
							style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
							onChange={onCheckBoxChange}
							value={'products'}
							checked={getCheckedValue('products', featurePermission)}
						/>
						<label className="permissionLabel" htmlFor={'allProducts'}>
							<span>{t('SETTING_USER_PERMISSIONS_PERMISSIONS_ALL_OPTIONS')}</span>
						</label>
					</div>
					<div style={{ marginTop: 5, marginBottom: 5 }}>
						<Divider />
					</div>
					{products.map((permission) => {
						return (
							<div key={permission.featureId} className="userAppPermission">
								<Checkbox
									id={permission.featureId}
									disabled={isDisabled}
									style={isDisabled ? undefined : { color: '#126CFC', paddingLeft: 0 }}
									onChange={onCheckBoxChange}
									value={permission.featureId}
									checked={getCheckedValue(permission.featureId, featurePermission)}
								/>

								<Tooltip
									title={
										<Typography style={{ color: '#ffffff' }}>{permission.description}</Typography>
									}
									placement="bottom"
								>
									<label className="permissionLabel" htmlFor={permission.featureId}>
										{permission.name}
									</label>
								</Tooltip>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default UserPermissionCheckBoxes;
